
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ApiService } from "../../api.service";
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from "../../shared/common.service";

@Component({
  selector: 'app-view-study',
  templateUrl: './view-study.component.html',
  styleUrls: ['./view-study.component.scss']
})
export class ViewStudyComponent implements OnInit {
  response: any;
  studyForm: FormGroup;
  searchParams: any = {};
  studyId: number;
  currentPage: number = 1;
  subjectCounter: number = 1;
  noOfVisits: number;
  studyName:string;
  frequencies: number[] = [];
  bufferValues: any[] = [];

  study_crc_id: number;
  subjects: any;
  userRole: string = '';
  canAddDeleteSubjects: boolean = true;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    // Get user role and check if user can add/delete subjects
    const userData = this.commonService.getUserData();
    this.userRole = userData?.designation || '';
    // CRO, Monitor, and Data Manager cannot add/delete subjects
    this.canAddDeleteSubjects = !['CRO', 'Monitor', 'Data Manager'].includes(this.userRole);
    
    this.study_crc_id = +this.route.snapshot.params.id;
    this.studyId = +this.route.snapshot.paramMap.get('id');

    // Capture the query parameters
    this.route.queryParams.subscribe(params => {
      this.currentPage = params['page'] || 1;
      this.searchParams.searchCrcName = params['searchCrcName'] || '';
      this.searchParams.searchStudyName = params['searchStudyName'] || '';
      this.searchParams.searchLocation = params['searchLocation'] || '';
      this.searchParams.searchAssignedBy = params['searchAssignedBy'] || '';
    });

    this.initializeForm();
    this.fetchSubjectsData();
  }

  initializeForm() {
    this.studyForm = this.fb.group({
      visibleSubjects: this.fb.array([])
    });
  }

  fetchSubjectsData(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timestamp = new Date().getTime();
      this.apiService.get(`api/getSubjectsData?study_crc_id=${this.studyId}&_=${timestamp}`).subscribe(
        (response: any) => {
          console.log("Fetched updated subjects data:", response);
          this.subjects = response.subjectsData;
          this.noOfVisits = response.no_of_visits;
          this.studyName = response.studyName;
          this.frequencies = response.frequencies || [];
          this.bufferValues = response.bufferValues || [];
          this.populateSubjects(response.subjectsData);
           // Disable all schedule_date fields except for the first one in each subject group
        this.visibleSubjects.controls.forEach((subjectGroup: FormGroup) => {
          const visits = subjectGroup.get('visits') as FormArray;
          visits.controls.forEach((visit: FormGroup, index: number) => {
            if (index === 0) {
              visit.get('schedule_date')?.enable(); // Only enable the first schedule date
            } else {
              visit.get('schedule_date')?.disable(); // Disable the rest
            }
          });
        });
          resolve(); // Resolve the Promise when all data processing is complete
        },
        error => {
          console.error('Error fetching subjects data:', error);
          reject(error); // Reject the Promise in case of an error
        }
      );
    });
  }




// Function to get the appropriate label based on visit index
getVisitLabel(index: number): string {
  if (index === 0) {
    return "Screening Visit"; // First visit
  } else if (index === 1) {
    return "Randomization Visit"; // Second visit
  } else {
    return `Visit ${index - 1}`; // For third visit onward
  }
}


 
  populateSubjects(subjectsData: any): void {
    const subjectsArray = this.visibleSubjects;
    subjectsArray.clear();

    if (subjectsData) {
      Object.keys(subjectsData).forEach((subjectCode, subjectIndex) => {
        const visitDataArray = subjectsData[subjectCode];
        const visitsArray = this.createVisitDatesFormArray(visitDataArray);
        
        const newSubjectGroup = this.fb.group({
          subjectName: [subjectCode],
          visits: this.fb.array(visitsArray),
          isEdited: [false] 
        });
        subjectsArray.push(newSubjectGroup);
  
        // Now subjectIndex is defined, so you can use it here
        visitsArray.forEach((visit, visitIndex) => {
          this.onVisitedDateChange(subjectIndex, visitIndex);
        });
      });
    }
  }


createVisitDatesFormArray(visitDataArray: any[]): FormGroup[] {
  const visitDatesArray: FormGroup[] = [];

  for (let i = 0; i < this.noOfVisits; i++) {
    const visitData = visitDataArray[i] || {}; 

    const visitGroup = this.fb.group({

      id: [visitData.id || null],

      schedule_date: [visitData.scheduled_date || ''],
      visited_date: [visitData.visited_date || ''],
      is_late: [this.calculateIsLate(visitData.scheduled_date, visitData.visited_date, i)]
    });

    visitDatesArray.push(visitGroup);
  }

  return visitDatesArray;
}


  // Helper to calculate if the visit is late
  calculateIsLate(scheduleDate: string, visitedDate: string, visitIndex: number): boolean {
    // if (!scheduleDate || !visitedDate || !this.bufferValues[visitIndex]) return false;


    if (!scheduleDate || !visitedDate || this.bufferValues[visitIndex] === undefined) return false;

    const schedule = new Date(scheduleDate);
    const visited = new Date(visitedDate);
    const bufferDays = this.bufferValues[visitIndex];

    const differenceInDays = Math.ceil((visited.getTime() - schedule.getTime()) / (1000 * 3600 * 24));


  // If buffer is 0, 
  if (bufferDays === 0) {
    return differenceInDays !== 0; 
  }


    return differenceInDays > bufferDays || differenceInDays < -bufferDays;
  }

  // Helper to add days to a date
  addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  get visibleSubjects(): FormArray {
    return this.studyForm.get('visibleSubjects') as FormArray;
  }


  onAddSubjectClick() {
    const newSubjectGroup = this.fb.group({
      subjectName: [''], 
      visits: this.fb.array(this.createEmptyVisitGroups(this.noOfVisits)),
      isEdited: [true] 
    });
    const visitsArray = newSubjectGroup.get('visits') as FormArray;

    // Disable all visited dates except the first one
    visitsArray.controls.forEach((visit, index) => {
      const scheduleDateControl = visit.get('schedule_date');
    if (index === 0) {
      scheduleDateControl?.enable(); // Enable the first schedule date
    } else {
      scheduleDateControl?.disable(); // Disable the rest
    }
        const visitedDateControl = visit.get('visited_date');
        if (index === 0) {
            visitedDateControl.enable(); // Enable only the first attended date
        } else {
            visitedDateControl.disable(); // Disable all other attended dates
        }
    });

  
    this.visibleSubjects.push(newSubjectGroup);
    this.subjectCounter++;
  }
  
onVisitedDateChange(subjectIndex: number, visitIndex: number) {
  const subjectGroup = this.visibleSubjects.at(subjectIndex) as FormGroup;
  const visits = subjectGroup.get('visits') as FormArray;

  // Enable the next visited_date only if the current one is filled
  if (visitIndex < visits.length - 1) {
      const currentVisit = visits.at(visitIndex);
      const nextVisit = visits.at(visitIndex + 1);

      if (currentVisit.get('visited_date')?.value) {
          nextVisit.get('visited_date')?.enable();
      } else {
          nextVisit.get('visited_date')?.disable();
      }
  }
  }


  createEmptyVisitGroups(visitCount: number): FormGroup[] {
    const visitGroups: FormGroup[] = [];

    for (let i = 0; i < visitCount; i++) {
      visitGroups.push(
        this.fb.group({
          schedule_date: [''],
          visited_date: [''],
          is_late: [false]
        })
      );
    }

    return visitGroups;
  }

  calculateVisitDates(subjectGroup: FormGroup) {
    const visits = subjectGroup.get('visits') as FormArray;
    const firstScheduleDate = visits.at(0).get('schedule_date')?.value;

    // Ensure only the first schedule date is enabled
    visits.controls.forEach((visit, index) => {
      if (index === 0) {
          visit.get('schedule_date')?.enable();  // Enable the first schedule date
      } else {
          visit.get('schedule_date')?.disable(); // Disable all subsequent schedule dates
      }
  });
    if (firstScheduleDate) {
      let baseDate = new Date(firstScheduleDate);

      for (let i = 1; i < visits.length; i++) {
        const frequencyDays = this.frequencies[i];
        if (frequencyDays) {
          const nextVisitDate = new Date(baseDate);
          nextVisitDate.setDate(baseDate.getDate() + frequencyDays);
          visits.at(i).get('schedule_date')?.setValue(nextVisitDate.toISOString().split('T')[0]);
          baseDate = nextVisitDate;
        }

        // Recalculate late status when schedule date or visited date changes
        const visitGroup = visits.at(i) as FormGroup;
        const scheduleDate = visitGroup.get('schedule_date')?.value;
        const visitedDate = visitGroup.get('visited_date')?.value;

        const isLate = this.calculateIsLate(scheduleDate, visitedDate, i);
        visitGroup.get('is_late')?.setValue(isLate);
      }
    }
  }

  onScheduleDateChange(subjectIndex: number) {
    const subjectGroup = this.visibleSubjects.at(subjectIndex) as FormGroup;
    this.calculateVisitDates(subjectGroup);
  }




  canEditVisitedDate(subjectIndex: number, visitIndex: number): boolean {
    const visitsArray = this.visibleSubjects.at(subjectIndex).get('visits') as FormArray;

    if (visitIndex === 0) {

      return true;
    }

    const previousVisit = visitsArray.at(visitIndex - 1);
    const previousVisitDate = previousVisit.get('visited_date')?.value;


    return previousVisitDate ? true : false;
  }

  onAttendedDateChange(subjectIndex: number, visitIndex: number) {
    const visitsArray = this.visibleSubjects.at(subjectIndex).get('visits') as FormArray;
    const currentVisit = visitsArray.at(visitIndex);
    const currentVisitedDate = currentVisit.get('visited_date')?.value;

    if (currentVisitedDate) {
      const scheduleDate = currentVisit.get('schedule_date')?.value;
      const isLate = this.calculateIsLate(scheduleDate, currentVisitedDate, visitIndex);
      currentVisit.get('is_late')?.setValue(isLate);
    }

    // this.recalculateVisitEditability(subjectIndex);
  }







  private recalculateVisitEditability(subjectIndex: number) {
    const visitsArray = this.visibleSubjects.at(subjectIndex).get('visits') as FormArray;
    for (let visitIndex = 1; visitIndex < visitsArray.length; visitIndex++) {
      const previousVisit = visitsArray.at(visitIndex - 1);
      const previousVisitDate = previousVisit.get('visited_date')?.value;

      // Disable/Enable based on previous visit's date
     /* if (!previousVisitDate) {
        visitsArray.at(visitIndex).get('visited_date').disable();
      } else {
        visitsArray.at(visitIndex).get('visited_date').enable();
      }*/
    }
  }


  onSubjectNameChange(subjectIndex: number): void {
    const subjectGroup = this.visibleSubjects.at(subjectIndex) as FormGroup;
    subjectGroup.get('isEdited')?.setValue(true);
  }


  onRemoveSubjectClick() {
    const subjectsArray = this.visibleSubjects;
  
    if (subjectsArray.length) {
      const lastSubjectIndex = subjectsArray.length - 1;
      const subjectToRemove = subjectsArray.at(lastSubjectIndex) as FormGroup;
  
      if (subjectToRemove.get('isEdited')?.value) {
     
        subjectsArray.removeAt(lastSubjectIndex);
        this.subjectCounter--;
      } else {
       
        const subjectCode = subjectToRemove.get('subjectName')?.value;
  
        this.apiService.delete(`api/deleteSubject/${subjectCode}`, { study_crc_id: this.study_crc_id }).subscribe(
          (response: any) => {
            alert(response.message);
            subjectsArray.removeAt(lastSubjectIndex);
            this.subjectCounter--;
          },
          error => {
            console.error('Deletion failed:', error);
            alert('Error occurred while deleting the subject.');
          }
        );
      }
    } else {
      alert("No subjects to remove.");
    }
  }
  


onSubmit() {
  

    let isValid = true;

    // Check if any subject name is empty
  const emptySubjectName = this.visibleSubjects.controls.some((subjectGroup: FormGroup) => {
    const subjectName = subjectGroup.get('subjectName')?.value;
    return !subjectName;
  });

  if (emptySubjectName) {
    alert('Please add a name of a subject.');
    return; // Stop the form submission
  }
  
  // Check if the first schedule_date is entered for each subject
  this.visibleSubjects.controls.forEach((subjectGroup: FormGroup) => {
    const visits = subjectGroup.get('visits') as FormArray;
    const firstVisit = visits.at(0);
    const scheduleDate = firstVisit.get('schedule_date')?.value;

    if (!scheduleDate) {
      isValid = false;
    }
  });

  if (!isValid) {
    alert('Please enter the first schedule date');
    return; // Stop the form submission
  }

    // Temporarily enable all schedule_date fields to include their values in formData
  this.visibleSubjects.controls.forEach((subjectGroup: FormGroup) => {
    const visits = subjectGroup.get('visits') as FormArray;
    visits.controls.forEach((visit: FormGroup) => {
      visit.get('schedule_date')?.enable({ emitEvent: false });
    });
  });
  const formData = this.studyForm.value;
  console.log('Form data before submission:', formData);

  
  const submissionData = formData.visibleSubjects.map((subject) => {
    const visits = subject.visits.map((visit) => {
      const scheduledDate = visit.schedule_date ? new Date(visit.schedule_date) : null;
      const formattedScheduledDate = scheduledDate?.toISOString().split('T')[0];

      const visitedDate = visit.visited_date ? new Date(visit.visited_date) : null;
      const formattedVisitedDate = visitedDate?.toISOString().split('T')[0];

      return {
        scheduled_date: formattedScheduledDate,
        visited_date: formattedVisitedDate,
        is_late: visit.is_late ? 1 : 0,
        study_crc_id: this.study_crc_id,
        id: visit.id || null
      };
    });

    return {
      subject_code: subject.subjectName,
      visits: visits,
      study_crc_id: this.study_crc_id
    };
  });


  this.apiService.post('api/submitVisits', { subjects: submissionData }).subscribe(
    response => {
      alert(response.message);
      this.fetchSubjectsData();


      // this.fetchSubjectsData().then(() => {
      //             this.visibleSubjects.controls.forEach((subjectGroup, index) => {
      //               subjectGroup.get('visits')['controls'].forEach((visitControl, visitIndex) => {
      //                 this.onVisitedDateChange(index, visitIndex);
      //               });
      //             });
      //           });



    },
    error => {
      console.error('Submission failed:', error);
      alert('Error occurred during submission.');
    }
  );
}



  goBack(): void {
    this.router.navigate(['/study-crc-list'], {
      queryParams: {
        page: this.currentPage,
        searchCrcName: this.searchParams.searchCrcName,
        searchStudyName: this.searchParams.searchStudyName,
        searchLocation: this.searchParams.searchLocation,
        searchAssignedBy: this.searchParams.searchAssignedBy
      }
    });
  }
}









